import { Injectable } from "@angular/core";
import { select, Store } from "@ngrx/store";
import {
	AbstractTranslatableAlertService,
	AUTH_MESSAGE_CODES,
	AUTH_MESSAGE_CONTEXT,
	AUTH_TRANSACTION_CODES,
	AUTH_TRANSACTION_MESSAGE_CONTEXT,
	AuthTransactionSelector,
	CANCEL_HINT_CODES,
	CORE_ROUTE,
	CoreState,
	FAIL_HINT_CODES,
	MessageService,
	NOOP_HINT_CODES,
	PERSONAL_NUMBER_CODES,
	REDIRECT_HASH,
	VerificationStatus,
} from "@obg/ngx.core";
import { TranslationGroup } from "@obg/ngx.core.content";
import { BrowserEventsService } from "@obg/ngx.global";
import { WindowRef } from "@obg/ngx.global.evo";
import { RouteAction, RouteSelector } from "@obg/ngx.router";
import { ignoreNil } from "@obg/rxjs";
import { BehaviorSubject, combineLatest, Observable } from "rxjs";
import { delay, distinctUntilChanged, filter, map, switchMap, take, tap, withLatestFrom } from "rxjs/operators";

import { EXCLUDED_GENERIC_ERROR_MESSAGE_CODES } from "../generic/generic-login.const";
import { LoginUiFlowService } from "../login-ui/login-ui-flow.service";
import { LoginQueryParams } from "../login.model";

import { SgaLoginConfigService } from "./sga-login.config";

export const SGA_LOGIN_FLOW_BANKID_NOT_OPENED_ALERT_ID = "SGA_LOGIN_FLOW_BANKID_NOT_OPENED_ALERT_ID";

@Injectable()
export class SgaLoginUiFlowService {
	pollingTimeoutSubject$: BehaviorSubject<boolean> = new BehaviorSubject(false);

	constructor(
		private store: Store<CoreState>,
		private messageService: MessageService,
		private authTransactionSelector: AuthTransactionSelector,
		private loginUiFlowService: LoginUiFlowService,
		private windowRef: WindowRef,
		private sgaLoginConfigService: SgaLoginConfigService,
		private browserEventsService: BrowserEventsService,
		private alertService: AbstractTranslatableAlertService
	) {}

	personalNumberErrors$(isQuickLogin?: boolean): Observable<string> {
		return this.messageService
			.consume$({
				messageCode: Object.values(PERSONAL_NUMBER_CODES),
				excludeCodes: isQuickLogin
					? []
					: [
							PERSONAL_NUMBER_CODES.validationErrorInvalidDate,
							PERSONAL_NUMBER_CODES.validationErrorInvalidCheckSum,
							PERSONAL_NUMBER_CODES.validationErrorPersonalNumberUnderage,
						],
			})
			.pipe(map(([error]) => error.messageCode));
	}

	initializeVerificationFail$(): Observable<void> {
		return this.messageService
			.consume$({
				context: [
					AUTH_TRANSACTION_MESSAGE_CONTEXT.initializeAuthTransaction,
					AUTH_TRANSACTION_MESSAGE_CONTEXT.initializeVerification,
				],
				messageCode: AUTH_TRANSACTION_CODES.authenticationError,
			})
			.pipe(map(() => undefined));
	}

	initializeAuthTransactionSuccess$(): Observable<void> {
		return this.messageService
			.consume$({
				context: [AUTH_TRANSACTION_MESSAGE_CONTEXT.initializeAuthTransaction],
				messageCode: AUTH_TRANSACTION_CODES.initializeAuthTransaction.success,
			})
			.pipe(map(() => undefined));
	}

	invalidCredentialsError$(): Observable<void> {
		return this.messageService
			.consume$({
				context: AUTH_MESSAGE_CONTEXT.loginWithToken,
				messageCode: AUTH_MESSAGE_CODES.login.invalidCredentials,
			})
			.pipe(map(() => undefined));
	}

	loginTransactionsVerificationStatus$(): Observable<VerificationStatus> {
		return this.store.pipe(
			select(this.authTransactionSelector.getLastStatus()),
			ignoreNil(),
			distinctUntilChanged()
		);
	}

	customerVerificationFail$(): Observable<void> {
		return this.messageService
			.consume$({
				context: AUTH_TRANSACTION_MESSAGE_CONTEXT.getVerificationStatus,
				excludeCodes: [
					...NOOP_HINT_CODES,
					...CANCEL_HINT_CODES,
					AUTH_TRANSACTION_CODES.verificationStatus.failed.alreadyUsed,
				],
			})
			.pipe(map(() => undefined));
	}

	customerVerificationCancel$(): Observable<void> {
		return this.messageService
			.consume$({
				context: AUTH_TRANSACTION_MESSAGE_CONTEXT.getVerificationStatus,
				excludeCodes: [...NOOP_HINT_CODES, ...FAIL_HINT_CODES],
			})
			.pipe(map(() => undefined));
	}

	cancelLastVerification$(): Observable<void> {
		return this.messageService
			.consume$({
				context: AUTH_TRANSACTION_MESSAGE_CONTEXT.cancelLastVerification,
			})
			.pipe(map(() => undefined));
	}

	genericLoginError$(): Observable<string> {
		return this.loginUiFlowService.genericLoginError$().pipe(
			filter(([error]) => !EXCLUDED_GENERIC_ERROR_MESSAGE_CODES.includes(error.messageCode)),
			map(([error]) => (navigator.onLine ? error.messageCode : AUTH_MESSAGE_CODES.login.error))
		);
	}

	openBankIdApp$(
		polling: boolean,
		isVerificationAborted: boolean,
		translationPrefix: string | undefined
	): Observable<void> {
		return this.store.select<LoginQueryParams | undefined>(RouteSelector.getQueryParams).pipe(
			take(1),
			switchMap((queryParams) =>
				this.store.pipe(
					select(this.authTransactionSelector.getBankIdDeepLink()),
					ignoreNil(),
					take(1),
					tap((bankId) => (this.windowRef.native.location.href = bankId.deepLink)),
					switchMap(() =>
						combineLatest([
							this.store
								.select(this.authTransactionSelector.getLastStatus())
								.pipe(
									filter(
										(status) =>
											status === VerificationStatus.Completed ||
											status === VerificationStatus.Failed
									)
								),
							this.store.select(RouteSelector.getFragment),
						]).pipe(
							take(1),
							tap(([status, fragement]) => {
								if (!fragement?.includes(REDIRECT_HASH)) {
									return;
								}
								if (status === VerificationStatus.Completed) {
									this.store.dispatch(
										RouteAction.navigate(CORE_ROUTE.auth.login, undefined, { queryParams })
									);
								} else {
									this.store.dispatch(RouteAction.back());
								}
							})
						)
					),
					filter(() => this.sgaLoginConfigService.getAppNotOpenedWarningEnabled()),
					delay(this.sgaLoginConfigService.getAppNotOpenedTimeout()),
					withLatestFrom(this.browserEventsService.visibility$),
					filter(([, isVisible]) => isVisible && polling && !isVerificationAborted),
					tap(() =>
						this.alertService.open(
							{
								id: SGA_LOGIN_FLOW_BANKID_NOT_OPENED_ALERT_ID,
								title: `${translationPrefix}.notification.title`,
								message: `${translationPrefix}.bankid-not-opened.message`,
								buttons: [{ text: "general.ok" }],
							},
							[TranslationGroup.Common]
						)
					)
				)
			),
			map(() => undefined)
		);
	}

	openNewBankIdFlowApp$(): Observable<any> {
		return this.store.select<LoginQueryParams | undefined>(RouteSelector.getQueryParams).pipe(
			tap(() => this.pollingTimeoutSubject$.next(false)),
			take(1),
			switchMap((queryParams) =>
				this.store.pipe(
					select(this.authTransactionSelector.getBankIdDeepLink()),
					ignoreNil(),
					take(1),
					tap((bankId) => (this.windowRef.native.location.href = bankId.deepLink)),
					delay(this.sgaLoginConfigService.getAppNotOpenedTimeout()),
					tap(() => this.pollingTimeoutSubject$.next(true)),
					switchMap(() =>
						combineLatest([
							this.store
								.select(this.authTransactionSelector.getLastStatus())
								.pipe(filter((status) => status === VerificationStatus.Completed)),
							this.store.select(RouteSelector.getFragment),
						]).pipe(
							take(1),
							tap(([status, fragement]) => {
								if (!fragement?.includes(REDIRECT_HASH)) {
									return;
								}
								if (status === VerificationStatus.Completed) {
									this.store.dispatch(
										RouteAction.navigate(CORE_ROUTE.auth.login, undefined, { queryParams })
									);
								}
							})
						)
					)
				)
			)
		);
	}
}
